/**
 * <site-credits> — the footer's "Site credits" reveal.
 *
 * Markup: sections/footer.liquid. The host wraps <footer> and the bar together;
 * the toggle lives inside the footer's legal line, the bar is the footer's
 * sibling. Styling and the lift itself are in assets/critical.css.
 *
 * WHAT ACTUALLY MOVES. The bar does not. It is `position: fixed` at the viewport
 * bottom, parked behind the footer, and opening lifts the PAGE off it. This file
 * only measures the bar and flips one attribute; critical.css owns which
 * elements translate and by how much.
 *
 * THIS COMPONENT DOES NOT CONSUME window.themeScroll, diverging from every
 * overlay in the theme, and the divergence is deliberate — the same call
 * assets/reveal.js records for the same reason. It does read scroll position, but
 * only `window.scrollY` inside a handler, which Lenis keeps accurate because it
 * scrolls the window; nothing here needs to LOCK scroll, and that is the only
 * thing the `themescroll:ready` handshake would be for. Taking it on would buy a
 * load-order dependency in exchange for nothing.
 *
 * THE MUTUAL-EXCLUSION CONTRACT, WHICH IS LOAD-BEARING AND NOT A PRECAUTION.
 * critical.css transforms <site-header> itself, and a transform creates a
 * containing block for `position: fixed` descendants — which <mega-menu>'s
 * panels are. That is only safe because a panel can never be open while the bar
 * is: a closed panel is opacity-0, pointer-events-none, clip-path-collapsed and
 * inert, so mis-anchoring one is unobservable. Break this and the mega menu
 * breaks, in a way that will look like nothing to do with the footer.
 *
 * Three handlers hold it, and each covers a case the others cannot:
 *
 *   - a document `pointerdown` outside the bar, which covers every click-opened
 *     overlay (mobile menu, search, cart, lightbox, enquiry popup) because the
 *     close fires before the trigger's own click handler;
 *   - a `pointerenter` on <site-header>, because the mega menu opens on HOVER and
 *     would otherwise never fire a pointerdown;
 *   - a scroll away from where the bar was opened, which is really a UX
 *     requirement (see handleScroll) but happens to close the keyboard route to
 *     the header as well.
 *
 * That is why no other component's JS was touched. Nothing calls `close()` on
 * this element, and nothing needs to.
 *
 * LOAD ORDER: independent. It reads no shared state and no other component
 * reads it.
 */
if (!customElements.get('site-credits')) {
  customElements.define(
    'site-credits',
    class SiteCredits extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('[data-credits-bar]');
        this.toggle = this.querySelector('[data-credits-toggle]');
        this.closeButton = this.querySelector('[data-credits-close]');

        if (!this.bar || !this.toggle || !this.closeButton) {
          console.warn('[site-credits] markup is incomplete; credits reveal disabled');
          return;
        }

        this.isOpen = false;

        this.handleToggleClick = this.handleToggleClick.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
        this.handleHeaderPointerEnter = this.handleHeaderPointerEnter.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleBarResize = this.handleBarResize.bind(this);

        this.toggle.addEventListener('click', this.handleToggleClick);
        this.closeButton.addEventListener('click', this.handleCloseClick);

        /**
         * The bar's height is content-driven — the merchant's rich text decides
         * it — so it is measured, never assumed. Re-measured on every open, and
         * while open by this observer, because a viewport resize can reflow one
         * line of copy into two and the lift has to follow it. The observer is
         * only ever connected while open.
         */
        if ('ResizeObserver' in window) {
          this.barResizeObserver = new ResizeObserver(this.handleBarResize);
        }

        this.setState(false);
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.handleToggleClick);
        this.closeButton?.removeEventListener('click', this.handleCloseClick);
        this.teardownOpenListeners();

        /**
         * The open state lives on <html>, outside this element, so a theme-editor
         * section re-render would otherwise strand it there — a permanently
         * lifted page with no bar under it.
         */
        document.documentElement.removeAttribute('data-credits');
        document.documentElement.style.removeProperty('--credits-shift');
      }

      handleToggleClick() {
        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      }

      handleCloseClick() {
        this.close();
      }

      handleKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        }
      }

      handleDocumentPointerDown(event) {
        /**
         * The toggle is excluded so its own click is not closed out from under
         * it: pointerdown would close, then click would re-open, and the bar
         * would appear to ignore every second press.
         */
        if (this.bar.contains(event.target)) return;
        if (this.toggle.contains(event.target)) return;
        this.close();
      }

      handleHeaderPointerEnter() {
        this.close();
      }

      /**
       * SCROLLING AWAY CLOSES IT. The bar belongs to the bottom of the page —
       * leaving it open while the visitor reads their way back up means arriving
       * at the footer again to find it already open, which reads as a bug rather
       * than as a state they left behind.
       *
       * The threshold is what makes this safe rather than twitchy: opening the
       * bar can itself shift scroll position by a pixel or two (focus moves, and
       * the lift changes what the browser considers the document bottom), and a
       * bare `scroll` listener would close the bar on the same gesture that
       * opened it. A few pixels of slop is below anything a person means as a
       * scroll and well above that noise.
       */
      handleScroll() {
        if (Math.abs(window.scrollY - this.scrollBaseline) > 4) this.close();
      }

      handleBarResize() {
        if (!this.isOpen) return;
        this.measure();
      }

      /** Publishes the bar's rendered height as the distance the page lifts. */
      measure() {
        document.documentElement.style.setProperty('--credits-shift', `${this.bar.offsetHeight}px`);
      }

      setState(open) {
        this.isOpen = open;

        if (open) this.measure();

        if (open) {
          document.documentElement.setAttribute('data-credits', 'open');
        } else {
          document.documentElement.removeAttribute('data-credits');
        }

        this.toggle.setAttribute('aria-expanded', String(open));
        this.bar.setAttribute('aria-hidden', String(!open));
        this.bar.inert = !open;
      }

      teardownOpenListeners() {
        document.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
        window.removeEventListener('scroll', this.handleScroll);
        document.querySelector('site-header')?.removeEventListener('pointerenter', this.handleHeaderPointerEnter);
        this.barResizeObserver?.disconnect();
      }

      open() {
        if (this.isOpen) return;

        this.setState(true);

        document.addEventListener('keydown', this.handleKeydown);
        this.barResizeObserver?.observe(this.bar);
        document.querySelector('site-header')?.addEventListener('pointerenter', this.handleHeaderPointerEnter);

        /**
         * `preventScroll` matters more than it looks: the bar sits below the
         * fold of the lifted page, and letting the browser scroll to it would
         * fight the lift it was opened by.
         */
        this.closeButton.focus({ preventScroll: true });

        /**
         * Deferred a frame so the click that opened the bar cannot immediately
         * close it again — the pointerdown that preceded it has already fired,
         * but a synthetic click (keyboard Enter on the toggle) can dispatch its
         * own pointer events in the same task on some browsers.
         */
        requestAnimationFrame(() => {
          if (!this.isOpen) return;
          document.addEventListener('pointerdown', this.handleDocumentPointerDown);

          /* Baseline is taken here, a frame in, for the same reason the listener
           * is: the lift has settled, so this is the position the visitor is
           * actually at rather than the one they were at mid-open. */
          this.scrollBaseline = window.scrollY;
          window.addEventListener('scroll', this.handleScroll, { passive: true });
        });
      }

      close() {
        if (!this.isOpen) return;

        /**
         * Read BEFORE setState, which sets `inert` on the bar — and inerting an
         * element containing the focused node moves focus to <body>, so asking
         * afterwards always answers "no" and focus would be silently dropped.
         */
        const focusWasInBar = this.bar.contains(document.activeElement);

        this.teardownOpenListeners();
        this.setState(false);

        /**
         * Focus only returns to the toggle if it was inside the bar. A close
         * caused by a pointerdown elsewhere on the page must not yank focus away
         * from whatever the visitor just reached for.
         */
        if (focusWasInBar) {
          this.toggle.focus({ preventScroll: true });
        }
      }
    },
  );
}
